/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import envConfig from '../config/envConfig';
import Button from './Button';
import { useNavigate } from 'react-router-dom';
import '../styles/EdgeHacks.css';
import GaneshaLogo from './GaneshaLogo';

function SignInForm({ onSubmit }) {
  const { theme } = useTheme();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const errorTimeouts = useRef({});
  const [showPassword, setShowPassword] = useState(false);

  const clearErrorAfterDelay = (field, delay = 4000) => {
    if (errorTimeouts.current[field]) clearTimeout(errorTimeouts.current[field]);
    errorTimeouts.current[field] = setTimeout(() => {
      setErrors(prev => ({ ...prev, [field]: '' }));
      delete errorTimeouts.current[field];
    }, delay);
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errs = {};
    if (!form.username.trim()) errs.username = "Username or email is required.";
    if (!form.password.trim()) errs.password = "Password is required.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      // Start fade out timers for errors
      for (const field of Object.keys(errs)) {
        clearErrorAfterDelay(field);
      }
      return;
    }

    setLoading(true);
    try {
      await onSubmit(form, setErrors); // Pass setErrors to handle server-side
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // For all current errors, start fadeout timer
    Object.keys(errors).forEach(field => {
      if (errors[field]) {
        clearErrorAfterDelay(field);
      }
    });
  }, [errors]);

  return (
    <div
      className="min-vh-100 d-flex flex-column align-items-center justify-content-center"
      style={{
        background: theme.gradientBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}
    >
      <div
        className="px-4 py-5 shadow-lg"
        style={{
          width: 400,
          maxWidth: '100%',
          background: '#fff',
          borderRadius: theme.cardRadius,
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
      >
        {/* Decorative top shape */}
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: theme.gradientBackground,
          opacity: 0.1
        }}></div>

        {/* Top Back Button */}
        <Button
          mode="secondary"
          size="sm"
          onClick={() => navigate('/')}
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: 20,
            left: 20,
            borderRadius: '50%',
            width: 38,
            height: 38,
            padding: 0,
            minWidth: 0,
            background: 'transparent',
            border: `1px solid ${theme.primary}20`,
            color: theme.primary
          }}
        >
          <i className="bi bi-arrow-left fs-5" />
        </Button>

        {/* Logo and App Name */}
        <div className="d-flex flex-column align-items-center mb-4 mt-2">
          <div className="p-3 rounded-circle mb-3" style={{ background: `${theme.primary}10` }}>
            <GaneshaLogo height={50} width={50} color={theme.primary} />
          </div>
          <h3
            className="mb-0"
            style={{
              color: theme.primary,
              fontWeight: 800,
              letterSpacing: '-0.5px'
            }}
          >
            {envConfig.appName}
          </h3>
          <p className="text-secondary small mb-0 fw-medium">Welcome Back</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-4">

          {/* Username/Email Field */}
          <div className="form-floating mb-3">
            <input
              type="text"
              id="username"
              name="username"
              className="form-control"
              placeholder="name@example.com"
              value={form.username}
              onChange={handleChange}
              style={{
                borderRadius: theme.buttonRadius,
                border: `1px solid ${theme.primary}20`,
                background: '#f8f9fa'
              }}
              autoComplete="username"
            />
            <label htmlFor="username" style={{ color: theme.primary }}>Username or Email</label>
            {errors.username && (
              <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                <i className="bi bi-exclamation-circle me-1"></i> {errors.username}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="form-floating mb-3 position-relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              className="form-control no-edge-eye"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              style={{
                borderRadius: theme.buttonRadius,
                border: `1px solid ${theme.primary}20`,
                background: '#f8f9fa',
                paddingRight: '40px'
              }}
              autoComplete="current-password"
            />
            <label htmlFor="password" style={{ color: theme.primary }}>Password</label>

            {/* Eye icon inside the input */}
            <span
              style={{
                position: "absolute",
                right: 15,
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: theme.primary,
                zIndex: 10
              }}
              onClick={() => setShowPassword(v => !v)}
            >
              <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
            </span>

            {errors.password && (
              <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                <i className="bi bi-exclamation-circle me-1"></i> {errors.password}
              </div>
            )}
          </div>

          {/* Forgot password link */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="form-check">
              {/* Optional Remember me placeholder */}
            </div>
            <a
              href="/forgot-password"
              className="text-decoration-none small fw-semibold"
              style={{ color: theme.secondary }}
            >
              Forgot password?
            </a>
          </div>

          {/* Sign In Button */}
          <div className="d-grid mb-4">
            <Button
              mode="primary" // Changed to primary for better CTA
              type="submit"
              disabled={loading}
              className="py-2 fs-6 fw-bold shadow-sm"
              style={{ letterSpacing: '0.5px' }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Signing In...
                </>
              ) : 'Sign In'}
            </Button>
          </div>

          <div className="text-center">
            <p className="small text-muted mb-0">
              Don't have an account?{' '}
              <span
                style={{ color: theme.primary, cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => navigate('/sign-up')}
              >
                Create one now
              </span>
            </p>
          </div>

        </form>
      </div>

      {/* Footer text */}
      <div className="mt-4 text-white opacity-75 small">
        &copy; {new Date().getFullYear()} {envConfig.appName}. All rights reserved.
      </div>
    </div>
  );
}

export default SignInForm;
